import { memo, useMemo } from 'react';
import {
  IoCalendarOutline,
  IoChatbubblesOutline,
  IoFileTrayOutline,
  IoGridOutline,
  IoLocationOutline,
  IoPauseCircleOutline,
} from 'react-icons/io5';
import {
  PATHWAY_LOGO_BLACK,
  PATHWAY_LOGO_WHITE,
} from '../../utils/constants/images';
import { usePermissions } from '../../contexts/PermissionContext';
import { useTheme } from '../../contexts/ThemeContext';
import { advancedSearchUrl as search } from '../enquiries/advancedFilters';
import CentreStatusWidget from './components/CentreStatusWidget';
import DashboardSection from './components/DashboardSection';
import StatCard from './components/StatCard';
import { DASHBOARD_SOURCE as S } from './dashboard.service';
import useDashboardData from './hooks/useDashboardData';
import './index.css';

const ACTIVE = { holdState: 'NotOnHold' };

const centreLabel = (centre) => centre.displayName ?? centre.centreName ?? String(centre.centreId);

// Visual sections mapped onto the data-source endpoints. One endpoint can feed several sections
// (e.g. enquiries → Overview, Follow-ups and On-hold), so each card names its own source.
// Every card links to the advanced search with the filters that reproduce its count (enquiries,
// same NZ windows and centre scope). `allCentresOnly` cards (no-centre counts) only apply to
// all-centre users; the API omits them otherwise.
const SECTIONS = [
  {
    id: 'overview',
    title: 'Enquiries overview',
    icon: <IoGridOutline />,
    description: 'See where families are on their journey.',
    cards: [
      { id: 'new', label: 'New enquiries', source: S.ENQUIRIES, field: 'newEnquiries', to: search({ status: 'Enquired', ...ACTIVE }) },
      { id: 'engaged', label: 'Engaged', source: S.ENQUIRIES, field: 'engaged', to: search({ status: 'Engaged', ...ACTIVE }) },
      { id: 'touring', label: 'Touring', source: S.ENQUIRIES, field: 'touring', to: search({ status: 'Touring', ...ACTIVE }) },
      { id: 'pending', label: 'Pending outcome', source: S.ENQUIRIES, field: 'pendingOutcome', to: search({ status: 'Pending', ...ACTIVE }) },
      { id: 'total', label: 'Total open', source: S.ENQUIRIES, field: 'totalOpen', to: search(ACTIVE) },
    ],
  },
  {
    id: 'follow-ups',
    title: 'Follow-ups',
    icon: <IoChatbubblesOutline />,
    description: 'Keep conversations moving and resolve outstanding items.',
    cards: [
      { id: 'unread', label: 'With unread messages', source: S.COMMUNICATIONS, field: 'unreadMessages', attention: true, to: search({ hasUnread: true }) },
      { id: 'failed', label: 'With failed comms (7 days)', source: S.COMMUNICATIONS, field: 'failedLast7Days', attention: true, to: search({ hasFailedCommunications: true }) },
      { id: 'duplicates', label: 'Potential duplicates', source: S.ENQUIRIES, field: 'potentialDuplicates', attention: true, to: search({ hasDuplicates: true, ...ACTIVE }) },
      { id: 'new-no-centre', label: 'New with no centre', source: S.ENQUIRIES, field: 'newWithNoCentre', attention: true, allCentresOnly: true, to: search({ status: 'Enquired', noCentre: true, ...ACTIVE }) },
      { id: 'engaged-no-centre', label: 'Engaged with no centre', source: S.ENQUIRIES, field: 'engagedWithNoCentre', attention: true, allCentresOnly: true, to: search({ status: 'Engaged', noCentre: true, ...ACTIVE }) },
    ],
  },
  {
    id: 'tours',
    title: 'Tours',
    icon: <IoCalendarOutline />,
    description: 'Plan upcoming visits and follow up with families.',
    cards: [
      { id: 'tours-today', label: 'Today', source: S.TOURS, field: 'today', to: search({ tourWindow: 'Today' }) },
      { id: 'tours-follow-up', label: 'Requiring follow-up', source: S.TOURS, field: 'requiringFollowUp', attention: true, to: search({ tourWindow: 'RequiringFollowUp' }) },
      { id: 'tours-this-week', label: 'This week', source: S.TOURS, field: 'thisWeek', to: search({ tourWindow: 'ThisWeek' }) },
      { id: 'tours-next-week', label: 'Next week', source: S.TOURS, field: 'nextWeek', to: search({ tourWindow: 'NextWeek' }) },
    ],
  },
  {
    id: 'on-hold',
    title: 'On-hold',
    icon: <IoPauseCircleOutline />,
    description: 'Reconnect when families are ready for their next step.',
    cards: [
      { id: 'off-hold-tomorrow', label: 'Off-hold tomorrow', source: S.ENQUIRIES, field: 'offHoldTomorrow', to: search({ holdState: 'OffHoldTomorrow' }) },
      { id: 'off-hold-week', label: 'Off-hold this week', source: S.ENQUIRIES, field: 'offHoldThisWeek', to: search({ holdState: 'OffHoldThisWeek' }) },
      { id: 'on-hold', label: 'On-hold', source: S.ENQUIRIES, field: 'onHold', to: search({ holdState: 'OnHold' }) },
      { id: 'waitlisted', label: 'Waitlisted', source: S.ENQUIRIES, field: 'waitlisted', to: search({ status: 'Waitlisted', ...ACTIVE }) },
    ],
  },
];

const ENQUIRY_HUB = {
  id: 'enquiry-hub',
  title: 'Enquiry hub',
  icon: <IoFileTrayOutline />,
  description: 'Respond to new enquiries and keep families moving forward.',
  cards: [
    { id: 'hub-new', label: 'New Enquiry Web Forms', source: S.ENQUIRIES, field: 'newVisitWithCentre', attention: true, to: search({ status: 'Enquired', source: 'VISIT', hasCentre: true, ...ACTIVE }) },
    { id: 'hub-tours-follow-up', label: 'Tours need Follow-up', source: S.TOURS, field: 'touringRequiringFollowUp', attention: true, to: search({ status: 'Touring', tourWindow: 'RequiringFollowUp', ...ACTIVE }) },
    { id: 'hub-unread-touring', label: 'Unread Touring', source: S.COMMUNICATIONS, field: 'unreadTouring', attention: true, to: search({ status: 'Touring', hasUnread: true, ...ACTIVE }) },
    { id: 'hub-unread-new', label: 'Unread New', source: S.COMMUNICATIONS, field: 'unreadNew', attention: true, to: search({ status: ['Enquired', 'Engaged'], hasUnread: true, ...ACTIVE }) },
    { id: 'hub-engaged', label: 'Engaged with centre', source: S.ENQUIRIES, field: 'engagedWithCentre', to: search({ status: 'Engaged', hasCentre: true, ...ACTIVE }) },
  ],
};

const MAX_TILE_CARDS = 4;

const isListSection = (section) => section.id === 'enquiry-hub'
  || (section.id === 'follow-ups' && section.cards.length > MAX_TILE_CARDS);

function buildSection(section, sources, hasAllCentreAccess, centreNames) {
  const cards = section.cards
    .filter((card) => hasAllCentreAccess || !card.allCentresOnly)
    .map((card) => {
      const state = sources[card.source];
      return {
        ...card,
        loading: state.loading,
        error: !state.data && Boolean(state.error),
        value: state.data?.[card.field],
        centreBreakdown: hasAllCentreAccess ? undefined : state.data?.centreBreakdown
          ?.filter((centre) => centreNames.has(String(centre.centreId).toLowerCase()))
          .map((centre) => ({
            centreId: centre.centreId,
            centreName: centreNames.get(String(centre.centreId).toLowerCase()),
            value: centre.counts?.[card.field],
          }))
          .sort((left, right) => left.centreName.localeCompare(right.centreName, 'en-NZ')),
        settled: !state.loading && Boolean(state.data),
      };
    })
    .filter((card) => !(card.allCentresOnly && card.settled && card.value == null));

  const sectionSources = [...new Set(cards.map((card) => card.source))];
  const failedSources = sectionSources.filter((key) => sources[key].error);
  const refreshing = sectionSources.some((key) => sources[key].refreshing);

  return { ...section, cards, failedSources, refreshing };
}

function HomePageContent() {
  const { darkMode } = useTheme();
  const { hasAllCentreAccess, centres, centresLoading } = usePermissions();
  const { sources, reload } = useDashboardData();

  const centreNames = useMemo(() => new Map(centres.map((centre) => [
    String(centre.centreId).toLowerCase(),
    centreLabel(centre),
  ])), [centres]);
  const sections = useMemo(() => SECTIONS.map((section) => buildSection(
    hasAllCentreAccess && section.id === 'tours' ? ENQUIRY_HUB : section,
    sources,
    hasAllCentreAccess,
    centreNames,
  )), [sources, hasAllCentreAccess, centreNames]);
  const [overview, ...rest] = sections;
  const centreStatus = sources[S.CENTRE_STATUS];

  const renderSection = (section) => (
    <DashboardSection
      key={section.id}
      id={section.id}
      title={section.title}
      icon={section.icon}
      description={section.description}
      className={`dashboard-section--${section.id}${isListSection(section) ? ' dashboard-section--list' : ''}`}
      failedSources={section.failedSources}
      refreshing={section.refreshing}
      onRetry={reload}
    >
      {section.cards.map((card) => (
        <StatCard
          key={card.id}
          id={card.id}
          label={card.label}
          value={card.value}
          centreBreakdown={card.centreBreakdown}
          showOverlapNote={card.source !== S.TOURS}
          loading={card.loading}
          error={card.error}
          to={card.to}
          attention={card.attention}
        />
      ))}
    </DashboardSection>
  );

  return (
    <div className='home-page'>
      <div className='home-container'>
        <header className='home-header'>
          <div className='home-header__content'>
            <p className='home-header__eyebrow'>Your daily overview</p>
            <h1 className='home-header__title'>Dashboard</h1>
            <p className='home-header__description'>
              {hasAllCentreAccess
                ? 'Enquiries, follow-ups and your enquiry hub. Know where to focus next.'
                : 'Enquiries, follow-ups and tours. Know where to focus next.'}
            </p>
            <span className='home-header__scope'>
              <IoLocationOutline aria-hidden='true' />
              <span aria-live='polite' aria-busy={!hasAllCentreAccess && centresLoading || undefined}>
                <span className='dashboard-sr-only'>Centre scope: </span>
                {hasAllCentreAccess
                  ? 'All centres'
                  : centresLoading
                    ? 'Loading centres...'
                    : centres.length === 1
                      ? centreLabel(centres[0])
                      : `${centres.length} authorised centres`}
              </span>
            </span>
          </div>
          <span className='home-icon-container'>
            <img
              src={darkMode ? PATHWAY_LOGO_WHITE : PATHWAY_LOGO_BLACK}
              className='home-icon'
              alt='Pathway'
            />
          </span>
        </header>

        <main className='dashboard' aria-label='Dashboard'>
          <div className={`dashboard__top ${centreStatus.data?.visible ? 'dashboard__top--with-status' : ''}`}>
            {renderSection(overview)}
            <CentreStatusWidget
              data={centreStatus.data}
              error={centreStatus.error}
              onRetry={() => reload(S.CENTRE_STATUS)}
            />
          </div>
          <div className='dashboard__columns'>
            {rest.map(renderSection)}
          </div>
        </main>
      </div>
    </div>
  );
}

export default memo(HomePageContent);
